import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ScoreBars, {
  ScoreStrengthFromValue
} from "@/components/controls/ScoreBars";
import { UserData } from "@/entities/users/types";
import { ArrowDown, ArrowUp } from "lucide-react";
import { cn } from "@/utils/utils";
import ScoringTableCardController, {
  ScoringTableCardControllerProps
} from "@/components/layouts/ScoringTableCard/ScoringTableCardController";
import Text from "@/components/ui/text";
import { OverallScoringData, SettingsPeriod } from "@/entities/reporting/types";
import { ChampionsScoringProvider } from "./ChampionsScoringContext";
import { useThresholds } from "@/entities/settings/hooks";
import { SettingsThresholdsType } from "@/entities/settings/types";

interface Props {
  leadUser: UserData | undefined;
  overallData: OverallScoringData;
  period: SettingsPeriod;
  isLoading?: boolean;
}

const ChampionsMapping: React.FC<Props> = ({
  leadUser,
  overallData,
  period,
  isLoading
}) => {
  const { t } = useTranslation();

  const { thresholds } = useThresholds();
  const scoreStrengthThresholds = useMemo(
    () => thresholds.find((th) => th.id === SettingsThresholdsType.CHAMPIONS_MAPPING_SCORE),
    [thresholds]
  );

  const dataTestPrefix = "champions";
  const overallScoreStrength = useMemo(
    () =>
      ScoreStrengthFromValue(
        overallData?.averageScore ?? 0,
        scoreStrengthThresholds
      ),
    [overallData, scoreStrengthThresholds]
  );
  const overallLabel = t(`champions.levels.${overallScoreStrength}`);
  const overallTrendUp = useMemo(
    () =>
      overallData?.trend.scoreForPeriod > overallData?.trend.comparedToScore,
    [overallData]
  );

  const championsProps: ScoringTableCardControllerProps = useMemo(
    () => ({
      dataTestPrefix: dataTestPrefix,
      labelProps: {
        title: t("champions.title"),
        subtitle: t("champions.subtitle"),
        empty: {
          title: t("champions.empty.title"),
          subtitle: t("champions.empty.subtitle")
        }
      },
      headerDataWidget: (
        <div
          data-test={`${dataTestPrefix}-overall-score`}
          className="min-w-28 w-28 min-h-20 h-20 flex flex-col flex-nowrap justify-start content-stretch align-start mx-1 my-2"
        >
          <Text as="div" variant="smBodySecondary" className="w-28 mx-2 mb-0.5">
            {t("champions.overall.title")}
          </Text>
          <Text as="div" variant="lgLabel" className="flex flex-row w-28 mx-2">
            <ScoreBars score={overallScoreStrength} />
            <span className="ml-2">{overallLabel}</span>
          </Text>
          <Text
            as="div"
            variant="smBodySecondary"
            className="flex flex-row w-28"
          >
            <span
              className={cn(
                "flex items-end justify-center size-5 mx-1",
                overallTrendUp ? "text-charts-positive" : "text-charts-negative"
              )}
            >
              {overallTrendUp ? <ArrowUp /> : <ArrowDown />}
            </span>
            <span className="self-center">
              {t("champions.overall.compared")}
            </span>
          </Text>
        </div>
      ),
      tableDef: {
        simpleColumns: [
          { name: t("champions.table.name") },
          { name: t("champions.table.kpi1"), size: 50 },
          { name: t("champions.table.kpi2"), size: 50 }
        ],
        summaryColumn: {
          header: t("champions.table.avgscore"),
          accessorKey: "summaryValue",
          size: 100,
          cell: ({ getValue }) => {
            const value = ScoreStrengthFromValue(
              getValue(),
              scoreStrengthThresholds
            );
            const label = t(`champions.levels.${value}`);
            return (
              <div className="flex items-center min-w-36 h-6">
                <ScoreBars score={value} />
                <span className="ml-2">{label}</span>
              </div>
            );
          },
          enableSorting: true,
          meta: {
            textAlign: "start"
          }
        },
        initialSummarySortDesc: false
      },
      isLoading
    }),
    [t, overallData, overallLabel, overallTrendUp, scoreStrengthThresholds]
  );

  return (
    <ChampionsScoringProvider leadUser={leadUser} period={period}>
      <ScoringTableCardController {...championsProps} />
    </ChampionsScoringProvider>
  );
};

export default ChampionsMapping;
